import type { FieldPath, FieldValues, RegisterOptions } from "react-hook-form";
import { useController } from "react-hook-form";

import type { InputAutocompleteProps } from "@/design-system/InputAutocomplete";
import { InputAutocomplete } from "@/design-system/InputAutocomplete";

import { FormErrorWrapper } from "./FormErrorWrapper";

interface FormInputAutocompleteProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> extends Omit<InputAutocompleteProps, "name" | "value"> {
  name: TName;
  rules?: RegisterOptions<TFormValues, TName>;
  onChange?: (value?: string) => void;
  "data-testid"?: string;
}
export function FormInputAutocomplete<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({
  "data-testid": dataTestid,
  name,
  rules,
  ...props
}: FormInputAutocompleteProps<TFormValues, TName>): React.ReactNode {
  const {
    field: { ref, ...field },
    fieldState: { error },
  } = useController<TFormValues, TName>({
    name,
    rules,
  });
  const maxLengthValue = typeof rules?.maxLength === "object" ? rules.maxLength.value : undefined;
  const isError = error != null;
  const currentValueLength = `${field.value || ""}`.length || 0;

  return (
    <FormErrorWrapper
      name={name}
      data-testid={dataTestid}
      subtext={
        maxLengthValue != null && (
          <>
            <span className={maxLengthValue < currentValueLength ? "text-text-danger-secondary" : undefined}>
              {currentValueLength}
            </span>
            /{maxLengthValue}
          </>
        )
      }
    >
      <InputAutocomplete
        {...props}
        {...field}
        value={(field.value as any) || ""}
        aria-invalid={isError}
        onChange={(e) => {
          field.onChange(e);
          props.onChange?.(e);
        }}
        onBlur={() => {
          field.onBlur();
          props.onBlur?.();
        }}
      />
    </FormErrorWrapper>
  );
}
